import { useNavigate } from '@tanstack/react-router';

import type { NotificationView } from '@/lib/api/generated/model';

import { notificationTarget } from '../deepLink';
import { useMarkRead } from './useMarkRead';

/**
 * Open what a notification is about, and mark it read (spec 131 FR-006): its
 * task's notifications all at once when it has a task, else just this one.
 * Shared by the inbox row and the alert's "Open" (spec 136).
 */
export function useOpenNotification() {
  const mark = useMarkRead();
  const navigate = useNavigate();
  return (notification: NotificationView) => {
    if (notification.task_id) {
      mark.mutate({ kind: 'task', taskId: notification.task_id });
    } else if (notification.read_at === null) {
      mark.mutate({ kind: 'one', notificationId: notification.id });
    }
    const target = notificationTarget(notification, notification.workflow_key);
    switch (target.kind) {
      case 'board':
        void navigate({
          to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
          params: {
            workspaceId: target.workspaceId,
            workflowKey: target.workflowKey,
          },
          search: { task: target.taskId, tab: target.tab },
          hash: target.hash,
        });
        return;
      case 'tasks':
        void navigate({
          to: '/workspaces/$workspaceId/tasks',
          params: { workspaceId: target.workspaceId },
          search: { task: target.taskId, tab: target.tab },
          hash: target.hash,
        });
        return;
      case 'usage':
        void navigate({
          to: '/workspaces/$workspaceId/ai-usage/spend',
          params: { workspaceId: target.workspaceId },
        });
        return;
      case 'triggers':
        void navigate({
          to: '/workspaces/$workspaceId/integrations/triggers',
          params: { workspaceId: target.workspaceId },
        });
        return;
      case 'connections':
        void navigate({
          to: '/workspaces/$workspaceId/integrations/connections',
          params: { workspaceId: target.workspaceId },
        });
        return;
      case 'commits':
        // The Linked accounts panel, scrolled into view by the router's hash
        // scrolling (spec 159 US2).
        void navigate({ to: '/settings/commits', hash: 'linked-accounts' });
        return;
      case 'workspace':
        void navigate({
          to: '/workspaces/$workspaceId',
          params: { workspaceId: target.workspaceId },
        });
        return;
      case 'settings':
        // A section, not a hash: the page scrolls once that section has been
        // read and rendered, which the router's hash scroll on arrival cannot
        // wait for (spec 193 US6). Both sections are on People (spec 194).
        void navigate({
          to: '/workspaces/$workspaceId/settings/people',
          params: { workspaceId: target.workspaceId },
          search: { section: target.section },
        });
        return;
      case 'admin-workspaces':
        void navigate({
          to: '/admin/workspaces',
          search: { show: 'without_owner' },
        });
        return;
      default: {
        // A target kind added without a route here fails to compile.
        const unhandled: never = target;
        return unhandled;
      }
    }
  };
}
